import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchBilling, requestBillingPortal } from '../lib/api';

const TIER_LABEL: Record<'free' | 'pro' | 'ultra', string> = {
  free: 'Free',
  pro: 'Pro',
  ultra: 'Ultra',
};

export function BillingSection() {
  const billing = useQuery({
    queryKey: ['billing', 'me'],
    queryFn: fetchBilling,
  });

  const portal = useMutation({
    mutationFn: requestBillingPortal,
    onSuccess: (data) => {
      window.location.href = data.portal_url;
    },
  });

  if (billing.isLoading) {
    return (
      <div className="settings-section settings-section-loading" role="status">
        Loading billing…
      </div>
    );
  }

  if (billing.isError || !billing.data) {
    return (
      <div className="settings-section settings-section-error" role="alert">
        Could not load billing info.
      </div>
    );
  }

  // Values-or-no-row (packet 007): a label never renders with an empty value —
  // either it carries a real value or the row is dropped. During the preview the
  // plan is Free, and we say so in words rather than showing a bare "Plan".
  const planLabel = TIER_LABEL[billing.data.plan_tier];
  const planValue =
    planLabel === 'Free' ? 'Free — while Tizok is in preview' : planLabel;
  const agentCount = billing.data.agent_count;

  return (
    <div className="settings-section">
      <h3>Billing</h3>
      <dl className="settings-dl">
        {planValue && (
          <>
            <dt>Plan</dt>
            <dd>{planValue}</dd>
          </>
        )}
        {agentCount != null && (
          <>
            <dt>Active agents</dt>
            <dd>{agentCount}</dd>
          </>
        )}
      </dl>
      {portal.isError && (
        <p className="settings-error" role="alert">
          Could not open the billing portal. Try again.
        </p>
      )}
      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-primary"
          onClick={() => portal.mutate()}
          disabled={portal.isPending}
        >
          {portal.isPending ? 'Opening…' : 'Manage billing (Stripe)'}
        </button>
      </div>
      <p className="settings-hint">
        Usage details are coming — you’ll see them here first.
      </p>
    </div>
  );
}
